import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as BABYLON from '@babylonjs/core'
import { StudioArt, StudioMode, WallSide } from '../types'
import { DEFAULT_ART_DEFAULTS, FRAME_PROFILES, STUDIO_COLORS } from '../constants'
import { clampU, generateLocalId, printHeight, wallGeometry, warmSpotlightColor } from '../babylon/sceneMath'
import { DEFAULT_CLASSIC_FRAME_ID, DEFAULT_CLASSIC_MAT_W } from '../classicCatalog'
import {
  artsClipWallCorners,
  artsCrowdedIntoCorners,
  artsHaveStackedPlacement,
  relayoutArtsAcrossRoom,
  redistributeArtsByWall,
  reflowArtsForRoom,
} from '../utils/sceneState'
import {
  configurePrintSpotLight,
  createWallPrintFixture,
  disposeWallPrintFixture,
  placeWallPrintFixture,
  WallPrintFixture,
} from '../sceneKit/galleryLightRig'
import { IMAGES } from '@/assets/images'

interface ArtMeshHandles {
  frame: BABYLON.Mesh
  mat: BABYLON.Mesh
  photo: BABYLON.Mesh
  spot: BABYLON.SpotLight
  fixture: WallPrintFixture
  frameMat: BABYLON.StandardMaterial
  matMat: BABYLON.StandardMaterial
  photoMat: BABYLON.StandardMaterial
}

export interface UseStudioArtsApi {
  arts: StudioArt[]
  selectedId: string | null
  selectedArt: StudioArt | null
  select: (id: string | null) => void
  hangAt: (side: WallSide, point: BABYLON.Vector3, photo: { img: string; egoId?: string; name?: string }) => StudioArt
  update: (id: string, patch: Partial<StudioArt>) => void
  remove: (id: string) => void
  toggleVisible: (id: string) => void
  swapPhoto: (id: string, photo: { img: string; egoId?: string }) => void
  /** Tears down every current print mesh and rebuilds from a fresh set — used by StudioFirstRunOverlay to hang a newly-picked album's photos over the placeholder seed. */
  replaceAllArts: (nextArts: StudioArt[]) => void
  /** Appends empty-slot placeholder `StudioArt`s (see the type's doc comment) until `arts` reaches `slotCount` — a no-op once it already does. Only meaningful in Classic mode; call after the loaded room resolves its real slot count. */
  padClassicSlots: (slotCount: number) => void
  /** Clamp freeform `u`/`y` into a room footprint after a procedural preset swap. */
  reflowToRoom: (dims: { width: number; height: number; depth: number }) => void
  getMeshesFor: (id: string) => BABYLON.Mesh[] | undefined
  getCenterFor: (id: string) => BABYLON.Vector3 | undefined
  isHidden: (id: string) => boolean
  /** Muse re-hang — honest heuristic (shuffles slide position + normalizes eye line), not a real AI call. */
  museReshuffle: () => void
}

const room = { width: 12, height: 5, depth: 16 }

/**
 * Geometry- and material-heavy per-print mesh CRUD. Mirrors the prototype's
 * buildArt/placeArt.
 *
 * `mode` gates all of the mesh-building below: in `'classic'` mode a loaded
 * `.babylon` room's print-slot meshes already have frame geometry baked in,
 * and `useStudioLegacyRoom` owns texturing them directly — this hook still
 * owns `arts`/`selectedId` state either way (so the Layers panel, photo
 * swapping, and command palette keep working uniformly), it just skips
 * ever building/placing/rebuilding its own frame+mat+photo meshes.
 */
export function useStudioArts(
  scene: BABYLON.Scene | null,
  initialArts: StudioArt[],
  roomDims: { width: number; height: number; depth: number } = room,
  mode: StudioMode = 'studio',
): UseStudioArtsApi {
  const [arts, setArts] = useState<StudioArt[]>(initialArts)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const meshesRef = useRef<Map<string, ArtMeshHandles>>(new Map())
  const textureCacheRef = useRef<Map<string, BABYLON.Texture>>(new Map())
  const highlightRef = useRef<BABYLON.HighlightLayer | null>(null)
  const artsRef = useRef<StudioArt[]>(initialArts)
  artsRef.current = arts

  const texFor = useCallback(
    (url: string, scn: BABYLON.Scene) => {
      const cache = textureCacheRef.current
      let tex = cache.get(url)
      if (!tex) {
        tex = new BABYLON.Texture(
          url,
          scn,
          false,
          true,
          BABYLON.Texture.TRILINEAR_SAMPLINGMODE,
          undefined,
          // A broken/expired signed photo URL used to leave the print
          // rendering solid black with no recovery — swap the same Texture
          // object over to the bundled placeholder in place so every
          // material already pointing at it (diffuse + emissive) picks up
          // the fallback without needing to be re-applied.
          () => {
            // eslint-disable-next-line no-console
            console.warn(`[gallery-studio-3d] failed to load print texture, falling back to placeholder: ${url}`)
            if (tex && tex.url !== IMAGES.topicPreviewPlaceholder) {
              tex.updateURL(IMAGES.topicPreviewPlaceholder)
            }
          },
        )
        tex.anisotropicFilteringLevel = 16
        cache.set(url, tex)
      }
      return tex
    },
    [],
  )

  // Mirrors the legacy renderer's SafelyDisposeMaterials() GC pass: sweep any
  // cached texture whose url no longer appears on a live art (removed,
  // swapped to a different photo, or replaced by Muse) instead of letting
  // them accumulate in GPU memory for the rest of the session.
  useEffect(() => {
    const referenced = new Set(arts.map((a) => a.img))
    const cache = textureCacheRef.current
    Array.from(cache.keys()).forEach((url) => {
      if (!referenced.has(url)) {
        cache.get(url)?.dispose()
        cache.delete(url)
      }
    })
  }, [arts])

  const disposeArtMeshes = useCallback((id: string) => {
    const handles = meshesRef.current.get(id)
    if (!handles) return
    handles.frame.dispose()
    handles.mat.dispose()
    handles.photo.dispose()
    handles.spot.dispose()
    disposeWallPrintFixture(handles.fixture)
    handles.frameMat.dispose()
    handles.matMat.dispose()
    handles.photoMat.dispose()
    meshesRef.current.delete(id)
  }, [])

  const placeMeshFor = useCallback(
    (art: StudioArt, handles: ArtMeshHandles) => {
      const profile = FRAME_PROFILES[art.profile]
      const { position, rotationY, normal } = wallGeometry(art.side, art.u, art.y, roomDims)
      handles.frame.position = position.add(normal.scale(profile.depth / 2))
      handles.frame.rotation.y = rotationY
      // Mat sits just past the frame front. Keep a real gap so the solid mat
      // slab doesn't z-fight the frame lip (~1mm was enough to tear on orbit).
      handles.mat.position = position.add(normal.scale(profile.depth + 0.02))
      handles.mat.rotation.y = rotationY
      // Photo must clearly clear the mat's front face (mat depth 0.014 → front
      // at d+0.027). The old d+0.032 left ~11mm and still tore inside the
      // window when zooming/orbiting (depth precision + solid mat under photo).
      const photoPos = position.add(normal.scale(profile.depth + 0.055))
      handles.photo.position = photoPos
      handles.photo.rotation.y = art.side === 0 ? 0 : rotationY + Math.PI

      // Beam originates at the can aperture and keys the print face — not a
      // wide wall-wash from deep in the room.
      const { spotPos, direction } = placeWallPrintFixture(
        handles.fixture,
        photoPos,
        normal,
        roomDims.height,
      )
      configurePrintSpotLight(handles.spot, spotPos, direction, art.spotI, warmSpotlightColor(art.warm))
      handles.fixture.tipMat.emissiveColor = warmSpotlightColor(art.warm).scale(0.9)
    },
    [roomDims],
  )

  const rebuildMeshFor = useCallback(
    (art: StudioArt, scn: BABYLON.Scene) => {
      disposeArtMeshes(art.id)
      const profile = FRAME_PROFILES[art.profile]
      const ph = printHeight(art)

      const frameMat = new BABYLON.StandardMaterial(`art-frame-${art.id}`, scn)
      frameMat.specularColor = BABYLON.Color3.FromHexString('#6b5a3a')
      frameMat.specularPower = 90
      frameMat.maxSimultaneousLights = 16
      frameMat.diffuseColor = BABYLON.Color3.FromHexString(art.finish)

      const matMat = new BABYLON.StandardMaterial(`art-mat-${art.id}`, scn)
      matMat.specularColor = BABYLON.Color3.Black()
      matMat.maxSimultaneousLights = 16
      matMat.diffuseColor = BABYLON.Color3.FromHexString(art.matTone)

      const photoMat = new BABYLON.StandardMaterial(`art-photo-${art.id}`, scn)
      // Soft specular — high power + shared emissive was amplifying sparkle that
      // looked like tearing next to the mat z-fight.
      photoMat.specularColor = BABYLON.Color3.FromHexString('#0a0a0a')
      photoMat.specularPower = 64
      photoMat.maxSimultaneousLights = 16
      // Bias the photo toward the camera in the depth buffer so a solid mat
      // slab underneath can't shred the image while the orbit camera moves.
      photoMat.zOffset = -2
      const tex = texFor(art.img, scn)
      photoMat.diffuseTexture = tex
      // Light fill from the fixture should read on the print — keep emissive
      // low so we don't fake a wall glow independent of the can.
      photoMat.emissiveTexture = tex
      photoMat.emissiveColor = BABYLON.Color3.FromHexString('#2a2a2a')

      const frame = BABYLON.MeshBuilder.CreateBox(
        `art-frame-mesh-${art.id}`,
        { width: art.w + 2 * (art.matW + profile.border), height: ph + 2 * (art.matW + profile.border), depth: profile.depth },
        scn,
      )
      frame.material = frameMat
      const mat = BABYLON.MeshBuilder.CreateBox(
        `art-mat-mesh-${art.id}`,
        // Thinner slab → less chance the front face shares depth with the photo.
        { width: art.w + 2 * art.matW, height: ph + 2 * art.matW, depth: 0.014 },
        scn,
      )
      mat.material = matMat
      const photo = BABYLON.MeshBuilder.CreatePlane(`art-photo-mesh-${art.id}`, { width: art.w, height: ph }, scn)
      photo.material = photoMat;
      [frame, mat, photo].forEach((m) => {
        m.metadata = { studioArtId: art.id }
      })

      const spot = new BABYLON.SpotLight(
        `art-spot-${art.id}`,
        BABYLON.Vector3.Zero(),
        new BABYLON.Vector3(0, -1, 0),
        Math.PI / 7,
        40,
        scn,
      )
      const fixture = createWallPrintFixture(scn, art.id)

      const handles: ArtMeshHandles = { frame, mat, photo, spot, fixture, frameMat, matMat, photoMat }
      meshesRef.current.set(art.id, handles)
      placeMeshFor(art, handles)
      // A rebuild (finish/frame/mat/photo change, or the initial boot pass)
      // always creates fresh, fully-enabled meshes — without this, a print
      // that was hidden before a reload (or before any patch that forces a
      // rebuild) would silently come back visible.
      if (art.hidden) {
        frame.setEnabled(false)
        mat.setEnabled(false)
        photo.setEnabled(false)
        spot.setEnabled(false)
        fixture.head.setEnabled(false)
        fixture.tip.setEnabled(false)
      }
      return handles
    },
    [disposeArtMeshes, placeMeshFor, texFor],
  )

  // Boot: build every seeded art once the scene exists. `arts` state was
  // seeded from `initialArts` back on this hook's very first render — which
  // happens the instant `Studio3DScreen` mounts, before `useStudio3DData`
  // has resolved the real source-album photos, since `scene` only exists
  // once the caller's `seedReady` gate flips true. Re-sync to whatever
  // `initialArts` holds *now* (the final, real seed) instead of trusting
  // that stale mount-time snapshot.
  useEffect(() => {
    if (!scene) return
    const meshes = meshesRef.current
    const textures = textureCacheRef.current
    highlightRef.current = new BABYLON.HighlightLayer('studio-hl', scene, { blurHorizontalSize: 0.7, blurVerticalSize: 0.7 })
    highlightRef.current.innerGlow = false

    // Repair bad placements from Classic stubs / over-aggressive spreads:
    // stacked same-slot piles, or frames jammed into corners (u past half-width).
    let seed = initialArts
    if (mode === 'studio') {
      if (artsHaveStackedPlacement(seed)) seed = relayoutArtsAcrossRoom(seed, roomDims)
      else if (artsClipWallCorners(seed, roomDims) || artsCrowdedIntoCorners(seed, roomDims)) {
        seed = redistributeArtsByWall(seed, roomDims, { resetEyeLine: true })
      }
    }
    artsRef.current = seed
    setArts(seed)
    // In classic mode the loaded `.babylon` room's own print-slot meshes are
    // textured directly by `useStudioLegacyRoom` — building our own
    // frame/mat/photo meshes here too would just float duplicate geometry
    // over that room.
    if (mode === 'studio') seed.forEach((art) => rebuildMeshFor(art, scene))

    return () => {
      Array.from(meshes.keys()).forEach((id) => disposeArtMeshes(id))
      textures.forEach((t) => t.dispose())
      textures.clear()
      highlightRef.current?.dispose()
      highlightRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene])

  // Re-place every print (and its ceiling spotlight) whenever the room's
  // live dimensions change — i.e. on a scene-preset swap. Without this,
  // switching Corridor -> Loft -> Pavilion updates `roomDims` but existing
  // prints keep the wall offsets/spotlight height computed for whichever
  // preset was active when they were last touched, until a user happens to
  // edit them individually (see placeMeshFor's roomDims.height usage).
  useEffect(() => {
    if (!scene || mode !== 'studio') return
    arts.forEach((art) => {
      const handles = meshesRef.current.get(art.id)
      if (handles) placeMeshFor(art, handles)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, roomDims, mode])

  // Live studio<->classic switches (not just the initial mount above): tear
  // down every studio-built print mesh on the way into Classic — its loaded
  // room owns texturing its own slot meshes instead — and rebuild them all
  // on the way back, since `arts` state (photos/names) persisted the whole
  // time but its 3D representation didn't exist while Classic was active.
  const prevModeRef = useRef(mode)
  useEffect(() => {
    if (!scene) return
    const prevMode = prevModeRef.current
    prevModeRef.current = mode
    if (prevMode === mode) return
    if (mode === 'classic') {
      Array.from(meshesRef.current.keys()).forEach((id) => disposeArtMeshes(id))
    } else {
      // Empty-slot placeholders from `padClassicSlots` only make sense while
      // that Classic room's own slot meshes are loaded to hold them — all
      // sharing the same default side/u/y. Drop ghosts, then relayout: Classic
      // never wrote real wall `u` (stubs are side:0,u:0), so rebuilding studio
      // meshes without this piles every print on one coincident frame.
      let next = artsRef.current.filter((a) => !a.isPlaceholder)
      if (artsHaveStackedPlacement(next) || next.some((a) => a.classicSlotIndex != null)) {
        next = relayoutArtsAcrossRoom(next, roomDims)
      }
      if (next !== artsRef.current) {
        artsRef.current = next
        setArts(next)
      }
      next.forEach((art) => rebuildMeshFor(art, scene))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, mode])

  const syncHighlight = useCallback((id: string | null) => {
    const hl = highlightRef.current
    if (!hl) return
    hl.removeAllMeshes()
    if (!id) return
    const handles = meshesRef.current.get(id)
    if (!handles) return
    hl.addMesh(handles.frame, BABYLON.Color3.FromHexString(STUDIO_COLORS.violet))
    hl.addMesh(handles.photo, BABYLON.Color3.FromHexString(STUDIO_COLORS.violet))
  }, [])

  const select = useCallback(
    (id: string | null) => {
      setSelectedId(id)
      syncHighlight(id)
    },
    [syncHighlight],
  )

  const update = useCallback(
    (id: string, patch: Partial<StudioArt>) => {
      if (!scene) return
      const geometryKeys: (keyof StudioArt)[] = ['w', 'profile', 'matW', 'ratio']
      const needsRebuild = geometryKeys.some((k) => k in patch)

      setArts((prev) =>
        prev.map((a) => {
          if (a.id !== id) return a
          const next = { ...a, ...patch }
          if ('u' in patch || 'w' in patch || 'side' in patch) {
            next.u = clampU(next.side, next.u, roomDims, next.w || 1.8)
          }
          const handles = meshesRef.current.get(id)
          if (!handles) return next

          if (needsRebuild) {
            const rebuilt = rebuildMeshFor(next, scene)
            if (selectedId === id) {
              const hl = highlightRef.current
              hl?.removeAllMeshes()
              hl?.addMesh(rebuilt.frame, BABYLON.Color3.FromHexString(STUDIO_COLORS.violet))
              hl?.addMesh(rebuilt.photo, BABYLON.Color3.FromHexString(STUDIO_COLORS.violet))
            }
            return next
          }

          if ('u' in patch || 'y' in patch || 'w' in patch || 'side' in patch) placeMeshFor(next, handles)
          if ('finish' in patch) handles.frameMat.diffuseColor = BABYLON.Color3.FromHexString(next.finish)
          if ('matTone' in patch) handles.matMat.diffuseColor = BABYLON.Color3.FromHexString(next.matTone)
          if ('spotI' in patch) handles.spot.intensity = next.spotI
          if ('warm' in patch) {
            const warm = warmSpotlightColor(next.warm)
            handles.spot.diffuse = warm
            handles.spot.specular = warm.scale(0.25)
            handles.fixture.tipMat.emissiveColor = warm.scale(0.9)
          }
          if ('img' in patch) {
            const tex = texFor(next.img, scene)
            handles.photoMat.diffuseTexture = tex
            handles.photoMat.emissiveTexture = tex
          }
          return next
        }),
      )
    },
    [scene, rebuildMeshFor, placeMeshFor, texFor, selectedId, roomDims],
  )

  // Explicit `isPlaceholder: false` — a swap onto a `padClassicSlots` stand-in
  // is exactly what promotes it to a real print; `update`'s plain object
  // spread would otherwise leave that flag (and the "Empty frame N" name)
  // untouched.
  const swapPhoto = useCallback(
    (id: string, photo: { img: string; egoId?: string }) => {
      const current = artsRef.current.find((a) => a.id === id)
      const rename = current?.isPlaceholder ? { name: current.name.replace(/^Empty frame/, 'Print') } : {}
      update(id, { ...photo, ...rename, isPlaceholder: false })
    },
    [update],
  )

  const hangAt = useCallback(
    (side: WallSide, point: BABYLON.Vector3, photo: { img: string; egoId?: string; name?: string }) => {
      const seq = artsRef.current.length + 1
      const id = generateLocalId('art')
      const u = side === 0 ? clampU(0, point.x, roomDims, 1.8) : clampU(side, point.z, roomDims, 1.8)
      const y = Math.max(1.5, Math.min(3.3, point.y))
      const art: StudioArt = {
        id,
        egoId: photo.egoId,
        img: photo.img,
        name: photo.name || `Untitled print ${seq}`,
        ratio: 4 / 3,
        w: 1.8,
        side,
        u,
        y,
        profile: 'gallery',
        finish: '#0d0b10',
        matW: 0.07,
        matTone: '#efe9dd',
        spotI: 9,
        warm: 0.7,
        loves: 0,
        commentsCount: 0,
      }
      setArts((prev) => [...prev, art])
      // Classic rooms have a fixed slot count baked into the loaded file —
      // nowhere procedural to hang a new print (and in practice the "hang"
      // tool never fires here anyway, since its wall picking only matches
      // the studio shell's `wall_*` mesh names — see useStudioTools).
      if (scene && mode === 'studio') rebuildMeshFor(art, scene)
      select(id)
      return art
    },
    [scene, rebuildMeshFor, select, roomDims, mode],
  )

  const replaceAllArts = useCallback(
    (nextArts: StudioArt[]) => {
      Array.from(meshesRef.current.keys()).forEach((id) => disposeArtMeshes(id))
      select(null)
      artsRef.current = nextArts
      setArts(nextArts)
      if (scene && mode === 'studio') nextArts.forEach((art) => rebuildMeshFor(art, scene))
    },
    [scene, mode, disposeArtMeshes, rebuildMeshFor, select],
  )

  // A Classic room's slot count (`useStudioLegacyRoom`'s `slotCount`) can
  // exceed however many real prints exist — e.g. a fresh gallery seeded with
  // 2 studio prints, switched into an 18-slot Classic variant. Without a
  // backing `StudioArt` for every slot, `applyPrintTextures` skips tagging
  // the extra meshes with `metadata.studioArtId` (see its own doc comment),
  // so `useStudioTools`' click-to-select can never resolve an id for them —
  // there's simply nothing to select, and the Assets panel's "select a
  // print first" toast can never be satisfied for those slots. Padding with
  // inert placeholders makes every slot pickable; `swapPhoto` promotes one to
  // a real print the instant a photo lands on it.
  const padClassicSlots = useCallback((slotCount: number) => {
    setArts((prev) => {
      // Stamp stable slot indices on existing rows first so texturing doesn't
      // depend on array order alone after undo/reorder.
      let changed = false
      const stamped = prev.map((a, i) => {
        if (a.classicSlotIndex != null) return a
        changed = true
        return { ...a, classicSlotIndex: i }
      })

      // Self-heal: switching to a smaller template (e.g. 18 -> 5 slots)
      // shouldn't leave a pile of "Empty frame N" ghosts from the bigger one
      // cluttering the Prints panel — trim surplus placeholders down to fit.
      // Real prints are NEVER dropped here, no matter how many there are
      // relative to the new slotCount (they just won't have a mesh to render
      // onto until a bigger template is picked again) — this only clears
      // slots that hold no photo.
      const real = stamped.filter((a) => !a.isPlaceholder)
      const placeholders = stamped.filter((a) => a.isPlaceholder)
      const placeholdersNeeded = Math.max(0, slotCount - real.length)
      const keptPlaceholders = placeholders.slice(0, placeholdersNeeded)
      const trimmed = keptPlaceholders.length !== placeholders.length
      let base = trimmed ? [...real, ...keptPlaceholders] : stamped
      if (trimmed) {
        changed = true
        // Re-stamp sequentially so surviving slots stay gapless for texturing.
        base = base.map((a, i) =>
          a.isPlaceholder ? { ...a, classicSlotIndex: i, name: `Empty frame ${i + 1}` } : { ...a, classicSlotIndex: i },
        )
      }

      if (base.length >= slotCount) {
        if (!changed) return prev
        artsRef.current = base
        return base
      }
      const additions: StudioArt[] = []
      for (let i = base.length; i < slotCount; i++) {
        additions.push({
          id: generateLocalId('art'),
          img: IMAGES.topicPreviewPlaceholder,
          name: `Empty frame ${i + 1}`,
          isPlaceholder: true,
          classicSlotIndex: i,
          side: 0,
          u: 0,
          y: 2.3,
          ...DEFAULT_ART_DEFAULTS,
          classicFrameId: DEFAULT_CLASSIC_FRAME_ID,
          classicMatW: DEFAULT_CLASSIC_MAT_W,
          loves: 0,
          commentsCount: 0,
        })
      }
      const next = [...base, ...additions]
      artsRef.current = next
      return next
    })
  }, [])

  const reflowToRoom = useCallback(
    (dims: { width: number; height: number; depth: number }) => {
      const next = reflowArtsForRoom(artsRef.current, dims)
      if (next === artsRef.current) return
      artsRef.current = next
      setArts(next)
      if (scene && mode === 'studio') {
        next.forEach((art) => {
          const handles = meshesRef.current.get(art.id)
          if (handles) placeMeshFor(art, handles)
        })
      }
    },
    [scene, mode, placeMeshFor],
  )

  const remove = useCallback(
    (id: string) => {
      disposeArtMeshes(id)
      setArts((prev) => prev.filter((a) => a.id !== id))
      if (selectedId === id) select(null)
    },
    [disposeArtMeshes, selectedId, select],
  )

  // `art.hidden` is the single source of truth (persisted with the rest of
  // `arts` in the scene note) — Studio mode also mirrors it onto the real
  // per-print meshes here so it takes effect immediately without waiting
  // for a rebuild; Classic mode has no mesh handles here at all
  // (`useStudioLegacyRoom` owns those), so the state write alone is enough —
  // that hook's own effect reacts to `arts` and hides the slot itself.
  const toggleVisible = useCallback((id: string) => {
    setArts((prev) =>
      prev.map((a) => {
        if (a.id !== id) return a
        const nextHidden = !a.hidden
        const handles = meshesRef.current.get(id)
        if (handles) {
          const visible = !nextHidden
          handles.frame.setEnabled(visible)
          handles.mat.setEnabled(visible)
          handles.photo.setEnabled(visible)
          handles.spot.setEnabled(visible)
          handles.fixture.head.setEnabled(visible)
          handles.fixture.tip.setEnabled(visible)
        }
        return { ...a, hidden: nextHidden }
      }),
    )
  }, [])

  const isHidden = useCallback(
    (id: string) => arts.find((a) => a.id === id)?.hidden ?? false,
    [arts],
  )

  const getMeshesFor = useCallback((id: string) => {
    const handles = meshesRef.current.get(id)
    return handles ? [handles.frame, handles.mat, handles.photo] : undefined
  }, [])

  const getCenterFor = useCallback(
    (id: string) => {
      const art = artsRef.current.find((a) => a.id === id)
      if (!art) return undefined
      // Classic slots live in authored `.babylon` space — procedural wallGeometry
      // misses them. Prefer the live mesh AABB center for tour/flyTo.
      if (mode === 'classic' && scene) {
        const mesh = scene.meshes.find((m) => m.metadata?.studioArtId === id)
        if (mesh) {
          const bb = mesh.getBoundingInfo().boundingBox
          return BABYLON.Vector3.Center(bb.minimumWorld, bb.maximumWorld)
        }
      }
      const profile = FRAME_PROFILES[art.profile]
      const { position, normal } = wallGeometry(art.side, art.u, art.y, roomDims)
      return position.add(normal.scale(profile.depth + 0.04))
    },
    [roomDims, mode, scene],
  )

  /**
   * Honest heuristic re-hang — shuffles each wall's prints along the slide
   * axis and normalizes eye line, same transform the prototype's "Muse"
   * button performs. No AI model is called; labelled as such in the UI
   * (StudioToolbar) per the phase-1 scope decision.
   */
  const museReshuffle = useCallback(() => {
    if (!scene) return
    // Always re-space (don't shuffle identical u's onto themselves — stacked
    // Classic leftovers used to look like Muse "did nothing").
    const next = redistributeArtsByWall(artsRef.current, roomDims, { shuffle: true, resetEyeLine: true })
    artsRef.current = next
    setArts(next)
    if (mode === 'studio') {
      next.forEach((art) => {
        const handles = meshesRef.current.get(art.id)
        if (handles) placeMeshFor(art, handles)
      })
    }
  }, [scene, roomDims, mode, placeMeshFor])

  const selectedArt = useMemo(() => arts.find((a) => a.id === selectedId) || null, [arts, selectedId])

  return useMemo(
    () => ({
      arts,
      selectedId,
      selectedArt,
      select,
      hangAt,
      update,
      remove,
      toggleVisible,
      swapPhoto,
      replaceAllArts,
      padClassicSlots,
      reflowToRoom,
      getMeshesFor,
      getCenterFor,
      isHidden,
      museReshuffle,
    }),
    [
      arts,
      selectedId,
      selectedArt,
      select,
      hangAt,
      update,
      remove,
      toggleVisible,
      swapPhoto,
      replaceAllArts,
      padClassicSlots,
      reflowToRoom,
      getMeshesFor,
      getCenterFor,
      isHidden,
      museReshuffle,
    ],
  )
}
